import Cookies from 'universal-cookie';
import axios from 'axios';
import { securityConsoleManager } from 'api/saseService';
import { redirectToAuthPortalLogout } from './utils/authPortal';
import { callInsightLogoutApi } from './utils/authPortalAuth';
import { clearIdentityCookiesAllScopes } from './utils/clearIdentityCookieScopes';
import { clearLayoutPreferencesSessionCache } from './utils/layoutPreferencesApi';
import { clearAllAccountContextState } from './utils/accountContext/accountContextUtils';
import { clearRumSessionIdentity } from './rum/rumAnalytics';
import { beginLogout } from './utils/logoutState';
import { TSM_BANNER_STORAGE_KEY } from './utils/tsmBannerStorage';

const cookies = new Cookies();

const closeSupportSession = async (insightUserId) => {
  const sessionId = cookies.get('_Id');

  if (!cookies.get('userSubRole') || !sessionId || !insightUserId) {
    return true;
  }

  try {
    const sessionResult = await axios.put(
      `${process.env.apiHost}supportuser/v1/supportsession/${sessionId}`,
      {
        sessionSt: '0',
        insightUserId,
        accountId: cookies.get('accountId')
      },
      {
        headers: {
          token: localStorage.getItem('token'),
          apikey: process.env.apiKey,
          accountId: cookies.get('accountId'),
          'Content-Type': 'application/json; charset=UTF-8'
        },
        withCredentials: true,
        timeout: 60000
      }
    );

    const supportUserResult = sessionResult?.data;
    return Boolean(
      supportUserResult?.response?.status ||
        supportUserResult?.response?.responseCode == '44018' ||
        supportUserResult?.response?.responseCode == '44013' ||
        supportUserResult?.response?.responseCode == '44031'
    );
  } catch (err) {
    console.warn('closeSupportSession failed:', err?.message || err);
    return false;
  }
};

/**
 * Shared helper function to close support session via API
 * @returns {Promise<Object>} Promise that resolves to the API response, or rejects with error
 */

export default {
  endSupportSession: function (insightUserId) {
    return closeSupportSession(insightUserId);
  },
  removeAccCookies: function () {
    let flag = false;
    return new Promise(async function (resolve, _reject) {
      // Step 1: Call Insight Cloud logout API first (then we clear cookies and redirect to Auth Portal).
      await callInsightLogoutApi();

      const sessionData = {
        sessionSt: '0',
        insightUserId: localStorage.getItem('supportSessionCustomerId') || localStorage.getItem('UserId'),
        accountId: cookies.get('accountId')
      };
      if (cookies.get('userSubRole')) {
        await axios
          .put(`${process.env.apiHost}supportuser/v1/supportsession/${cookies.get('_Id')}`, sessionData, {
            headers: {
              token: localStorage.getItem('token'),
              apikey: process.env.apiKey,
              accountId: cookies.get('accountId'),
              'Content-Type': 'application/json; charset=UTF-8'
            },
            timeout: 60000
          })
          .then(async (sessionResult) => {
            const supportUserResult = sessionResult.data;
            if (
              supportUserResult.response.status ||
              supportUserResult.response.responseCode == '44018' ||
              supportUserResult.response.responseCode == '44013' ||
              supportUserResult.response.responseCode == '44031'
            ) {
              flag = true;
              clearCookies();
              resolve(true);
            }
          })
          .catch((err) => {
            console.warn('supportsession close failed during logout:', err?.message || err);
          });
      } else {
        flag = true;
        clearCookies();
        resolve(true);
      }
      if (!flag) {
        clearCookies();
        redirectToAuthPortalLogout();
        resolve(true);
      }
    });
  },
  exitSupportUserSession: function () {
    return new Promise(async function (resolve, reject) {
      const activeSupportCustomerId = localStorage.getItem('supportSessionCustomerId') || localStorage.getItem('UserId');
      const sessionClosed = await closeSupportSession(activeSupportCustomerId);
      if (!sessionClosed) {
        reject(new Error('Unable to close support session'));
        return;
      }

      clearCookies();
      resolve(true);
    });
  }
};

export const clearCookies = () => {
  // Mark the logout window here, atomically with the clear itself (PRJCBUGEN-65938). From this
  // point the app has no session cookies but has not navigated away yet, so AuthGuard would
  // otherwise redirect to the auth portal LOGIN url and override the LOGOUT navigation that
  // follows. Set here rather than at the start of logout so it cannot lapse while the logout and
  // support-session APIs (60s timeout each) are in flight. The marker is in memory only, so the
  // support-agent path that calls clearCookies() and then reloads is unaffected.
  beginLogout();

  // Close all security console windows on logout
  securityConsoleManager.closeAllWindows();

  clearRumSessionIdentity();
  clearLayoutPreferencesSessionCache(cookies.get('_Id'));
  clearAllAccountContextState();
  // UI preferences (no session or account data) that should survive logout. Best-effort: a storage
  // failure only loses the preference and must never stop the session cleanup below.
  let preservedPreferences = [];
  try {
    preservedPreferences = [TSM_BANNER_STORAGE_KEY].map((key) => [key, localStorage.getItem(key)]).filter(([, value]) => value !== null);
  } catch (e) {
    preservedPreferences = [];
  }
  localStorage.clear();
  preservedPreferences.forEach(([key, value]) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Preference is simply not kept
    }
  });
  clearIdentityCookiesAllScopes(cookies);
  const removeCookieOpts = { path: '/', secure: process.env.secureCoockie };
  cookies.remove('accountId', removeCookieOpts);
  cookies.remove('userRole', removeCookieOpts);
  cookies.remove('userAccountId', removeCookieOpts);
  cookies.remove('_Id', removeCookieOpts);
  cookies.remove('token', removeCookieOpts);
  cookies.remove('isCountryDisabled', removeCookieOpts);
  cookies.remove('_accountsGL', removeCookieOpts);
  cookies.remove('_accountsPLL', removeCookieOpts);
  cookies.remove('firstName', removeCookieOpts);
  cookies.remove('lastName', removeCookieOpts);
  cookies.remove('toggleSrc', removeCookieOpts);
  cookies.remove('apiKey', removeCookieOpts);
  cookies.remove('existingProAcnt', removeCookieOpts);
  cookies.remove('userSubRole', removeCookieOpts);
  // supportAccessSt may be set without path in MainLayout (route-scoped); remove all common variants.
  cookies.remove('supportAccessSt');
  cookies.remove('supportAccessSt', removeCookieOpts);
  cookies.remove('supportAccessSt', { domain: '.netgear.com' });
  cookies.remove('supportAccessSt', { path: '/', domain: '.netgear.com' });
  // Clear redirect/session markers used by classic bootstrap flow.
  // Remove them across common scope variants so stale cookies cannot trigger auto-login APIs post logout.
  cookies.remove('redirectCookies');
  cookies.remove('redirectCookies', { path: '/' });
  cookies.remove('redirectCookies', { domain: '.netgear.com' });
  cookies.remove('redirectCookies', { path: '/', domain: '.netgear.com' });
  cookies.remove('sessionActive');
  cookies.remove('sessionActive', { path: '/' });
  cookies.remove('sessionActive', { domain: '.netgear.com' });
  cookies.remove('sessionActive', { path: '/', domain: '.netgear.com' });
};
